import type { BaseContract } from 'ethers';
import { useEffect, useMemo, useRef } from 'react';

import { useWeb3 } from '../../../components/Web3/Web3Provider/useWeb3';
import { ContractConnection } from '../../../types/web3/contracts';
import { ErcChainId } from '../../../types/web3/web3';
import { getErcChainId } from '../../../utils/web3/chains';
import { useBlockchain } from './useBlockchain';

interface Props {
  address?: string;
  abi: any[];
  chainId?: ErcChainId;
}

// a helper to cache contract connection
export const useContract = <T extends BaseContract>(props: Props) => {
  const web3 = useWeb3();
  const { abi, address, chainId } = props;
  const { createContractConnection } = useBlockchain();
  const connectionRef = useRef<ContractConnection | null>(null);

  const clearConnection = () => {
    connectionRef.current?.contract?.removeAllListeners();
  };

  const createConnection = () => {
    clearConnection(); // clear the last connection from events
    if (!address || !chainId) return null;

    return createContractConnection<T>(address, abi, getErcChainId(chainId)!);
  };

  const connection = useMemo<ContractConnection<T> | null>(createConnection, [
    !!web3.web3Provider,
    web3.chainId,
    chainId,
    address,
  ]);
  connectionRef.current = connection;

  // when the contract is not in use, clear the listeners if any to prevent memory leaks
  useEffect(() => clearConnection, []);

  return connection;
};
